import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Card,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  People,
  Public,
  MoreVert,
  Share,
  Flag,
  Edit,
  Delete,
} from '@mui/icons-material';
import { Community } from '../../types/community';
import { toast } from 'react-hot-toast';
import ReportCommunityModal from '../Admin/ReportCommunityModal'; 

interface CommunityHeaderProps {
  community: Community;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
  glassEffect: any;
  navigate: any;
  handleJoinLeave: () => void;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  handleMenuClose: () => void;
  anchorEl: HTMLElement | null;
  isOwner: boolean;
  showJoinButton: boolean;
  showLeaveButton: boolean;
  showEditButton: boolean;
  handleEditCommunity: () => void;
  isMobile: boolean;
}

const CommunityHeader: React.FC<CommunityHeaderProps> = ({
  community,
  isDark,
  goldColor,
  deepGreen,
  glassEffect,
  navigate,
  handleJoinLeave,
  handleMenuOpen,
  handleMenuClose,
  anchorEl,
  isOwner,
  showJoinButton,
  showLeaveButton,
  showEditButton,
  handleEditCommunity,
  isMobile,
}) => {
  // State for report modal
  const [reportModalOpen, setReportModalOpen] = useState(false);

  const handleShareCommunity = () => {
    const communityLink = `${window.location.origin}/community/${community.slug}`;
    navigator.clipboard.writeText(communityLink)
      .then(() => {
        toast.success('Community link copied to clipboard!');
      })
      .catch((err) => {
        console.error('Failed to copy link: ', err);
        toast.error('Failed to copy link');
      });
    handleMenuClose();
  };

  // Handle report community from menu
  const handleReportCommunity = () => {
    setReportModalOpen(true);
    handleMenuClose();
  };

  // Handle successful report submission
  const handleReportSuccess = () => {
    toast.success('Report submitted successfully! Our team will review it.');
  };

  // Prepare community data for the modal
  const communityDataForReport = {
    id: community.id,
    name: community.name,
    description: community.description,
    owner: {
      id: community.owner.id,
      username: community.owner.username,
      profilePicture: community.owner.profilePicture,
    },
    membersCount: community.membersCount,
    createdAt: community.createdAt,
    slug: community.slug,
    profilePicture: community.profilePicture,
    bannerImage: community.bannerImage,
  };

  return (
    <>
      <Card sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: { xs: 2, md: 4 }, 
        borderRadius: '20px', 
        ...glassEffect,
        minHeight: { xs: 'auto', md: '140px' },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, md: 3 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, flex: 1, width: '100%' }}>
          <Avatar
            src={community.profilePicture}
            sx={{
              width: { xs: 50, sm: 60, md: 80 },
              height: { xs: 50, sm: 60, md: 80 },
              border: `4px solid ${isDark ? deepGreen : '#fff'}`,
              bgcolor: goldColor,
              color: deepGreen,
              fontWeight: 700,
              fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
            }}
          >
            {community.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 800,
                fontSize: { xs: '1.3rem', sm: '1.8rem', md: '2.125rem' }
              }}>
                {community.name}
              </Typography>
              {isOwner && (
                <Chip
                  label="Owner"
                  size="small"
                  sx={{
                    bgcolor: alpha(goldColor, 0.2),
                    color: goldColor,
                    fontWeight: 700,
                    border: `1px solid ${goldColor}`,
                    px: { xs: 1, md: 1.5 },
                    py: 0.5,
                    fontSize: { xs: '0.6rem', md: '0.7rem' },
                    height: { xs: 20, md: 24 }
                  }}
                />
              )}
            </Box>
            <Typography variant="h6" sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              mb: 1,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }
            }}>
              wfh/{community.slug}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                icon={<People sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />}
                label={`${community.membersCount.toLocaleString()} members`}
                size="small"
                sx={{
                  bgcolor: alpha(goldColor, 0.1),
                  color: goldColor,
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  height: { xs: 24, md: 32 }
                }}
              />
              <Chip
                icon={<Public sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }} />}
                label="Public"
                size="small"
                sx={{
                  bgcolor: isDark ? alpha('#fff', 0.1) : alpha(deepGreen, 0.1),
                  color: isDark ? '#fff' : deepGreen,
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  height: { xs: 24, md: 32 }
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, md: 2 }, 
          alignItems: 'center', 
          flexShrink: 0,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-start' }
        }}>
          {/* Edit Button for Owners */}
          {showEditButton && (
            <Button
              variant="outlined"
              onClick={handleEditCommunity}
              startIcon={<Edit />}
              sx={{
                borderRadius: '20px',
                bgcolor: 'transparent',
                color: goldColor,
                borderColor: goldColor,
                fontWeight: 700,
                px: { xs: 2, md: 3 },
                minWidth: { xs: '120px', sm: '140px' },
                transition: 'all 0.2s ease',
                fontSize: { xs: '0.8rem', md: '0.9rem' },
                '&:hover': {
                  bgcolor: alpha(goldColor, 0.1),
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Edit
            </Button>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {showJoinButton && (
              <Button
                variant="contained"
                onClick={handleJoinLeave}
                sx={{
                  borderRadius: '20px',
                  bgcolor: goldColor,
                  color: deepGreen,
                  borderColor: goldColor,
                  fontWeight: 700,
                  px: { xs: 3, md: 4 },
                  minWidth: { xs: '140px', sm: '180px', md: '200px' },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(goldColor, 0.9),
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Join
              </Button>
            )}
            
            {showLeaveButton && (
              <Button
                variant="outlined"
                onClick={handleJoinLeave}
                sx={{
                  borderRadius: '20px',
                  bgcolor: 'transparent',
                  color: goldColor,
                  borderColor: goldColor,
                  fontWeight: 700,
                  px: { xs: 3, md: 4 },
                  minWidth: { xs: '140px', sm: '180px', md: '200px' },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: alpha(goldColor, 0.1),
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Leave
              </Button>
            )}
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                border: `1px solid ${alpha(goldColor, 0.3)}`,
                color: goldColor,
                '&:hover': {
                  bgcolor: alpha(goldColor, 0.1)
                }
              }}
            >
              <MoreVert sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: {
                ...glassEffect,
                borderRadius: '12px',
                mt: 1,
                minWidth: '180px'
              }
            }}
          >
            {/* Edit Community Option in Menu for Owners */}
            {showEditButton && (
              <MenuItem onClick={() => { handleEditCommunity(); handleMenuClose(); }}>
                <ListItemIcon>
                  <Edit fontSize="small" sx={{ color: goldColor }} />
                </ListItemIcon>
                <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Edit Community</ListItemText>
              </MenuItem>
            )}
            
            {showEditButton && (
              <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
            )}
            
            <MenuItem onClick={handleShareCommunity}>
              <ListItemIcon>
                <Share fontSize="small" sx={{ color: goldColor }} />
              </ListItemIcon>
              <ListItemText sx={{ color: isDark ? '#fff' : deepGreen }}>Share Community</ListItemText>
            </MenuItem>
            
            {/* Delete Community Option for Owners */}
            {isOwner && (
              <>
                <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
                <MenuItem onClick={handleMenuClose} sx={{ color: '#ff4444' }}>
                  <ListItemIcon>
                    <Delete fontSize="small" sx={{ color: '#ff4444' }} />
                  </ListItemIcon>
                  <ListItemText>Delete Community</ListItemText>
                </MenuItem>
              </>
            )}
            
            <Divider sx={{ borderColor: alpha(goldColor, 0.1) }} />
            {/* Report Community Option */}
            <MenuItem onClick={handleReportCommunity} sx={{ color: '#ff4444' }}>
              <ListItemIcon>
                <Flag fontSize="small" sx={{ color: '#ff4444' }} />
              </ListItemIcon>
              <ListItemText>Report Community</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Card>

      {/* Report Community Modal */}
      <ReportCommunityModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onSuccess={handleReportSuccess}
        community={communityDataForReport}
      />
    </>
  );
};

export default CommunityHeader;